import React from 'react';
import { COLORS } from 'Components/utils/color/colors';
import { useAppContext } from 'Context/ReactAppContext';
import { getCurrencySymbol } from 'Components/utils/price/price.util';
import { Badge } from 'Components/frameworks/badge/Badge';
import { TemplateString } from 'Components/frameworks/template-string/TemplateString';
import { Title } from 'Components/frameworks/title/Title';
import { t, getKeyValue } from 'Src/i18n';
import type { ProductFeatureDetailServiceItemProps } from './ProductFeatureDetailServiceItem';
import { ProductFeatureDetailServiceItem } from './ProductFeatureDetailServiceItem';
export type ProductFeatureDetailServiceProps = {
  badges?: Array<{
    label?: string;
    iconName: IconName;
  }>;
  className?: string;
  color?: Colors;
  duration?: {
    description: string;
    label?: string;
  };
  id?: string;
  items?: Array<ProductFeatureDetailServiceItemProps>;
  level?: string;
  note?: string;
  targetPrice?: {
    currency: string;
    label: string;
    price: number;
  };
  title: string;
};
export const ProductFeatureDetailService = ({
  badges = [],
  className,
  color = COLORS.BLACK,
  duration,
  id,
  items = [],
  level,
  note,
  targetPrice,
  title,
}: ProductFeatureDetailServiceProps) => {
  const { locale } = useAppContext();

  if (!title) {
    return null;
  }

  const instalmentValue = getKeyValue('common.price.instalmentValue');
  return (
    <article className={className} data-id={id}>
      <Title hLevel={2} theme="title-sans-n3">
        {title}
      </Title>
      <Title hLevel={3} theme="title-sans-n2">
        {level}
      </Title>
      {targetPrice?.price && (
        <TemplateString
          className="block py-10 text-14"
          template={targetPrice.label}
          values={{
            currency: targetPrice.currency ? getCurrencySymbol(locale, targetPrice.currency) : '',
            price: getKeyValue('common.price.displayInstalmentsPrice')
              ? t('common.price.instalmentPrice', {
                  instalmentValue,
                  dividedPrice: Math.floor(targetPrice.price / instalmentValue),
                })
              : Math.floor(targetPrice.price) || '',
          }}
          valuesClassName={{
            currency: `text-20 text-${color} font-bold`,
            price: `text-20 text-${color} font-bold`,
          }}
        />
      )}
      {duration?.description && (
        <div className="text-1 my-10">
          {duration.label ? <span className="font-bold">{`${duration.label}: `}</span> : null}
          {duration.description}
        </div>
      )}
      {badges.map(({ iconName, label }) => (
        <div className="my-10 flex items-center gap-10" key={iconName}>
          <Badge
            className="size-50 border-saffron bg-saffron text-black"
            iconName={iconName}
            iconSize="2.375rem"
            variant="pill"
          />
          <span className="font-brand text-16 text-black">{label}</span>
        </div>
      ))}
      {!!items.length && (
        <ul className="list-none p-0 text-14 leading-normal text-black">
          {items.map((item) => (
            <ProductFeatureDetailServiceItem
              className="my-5 list-disc py-10"
              description={item.description}
              items={item.items}
              key={item.description}
              label={item.label}
              note={item.note}
              tag={item.tag}
            />
          ))}
        </ul>
      )}
      {!!note && <div className="mt-15 text-12 italic">{note}</div>}
    </article>
  );
};
